import { useMemo, useState } from 'react';
import { Container, Media } from 'reactstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { getUnits, getUnitByName } from '../shared/unitInfo';
import { useDesktopAutofocus } from '../hooks/useDesktopAutofocus';
import SearchInput from './SearchInput';
import BackToList from './BackToList';
import '../App.css';

const SECTIONS = [
    { id: 'overview', label: 'Overview' },
    { id: 'stats', label: 'Stats' },
    { id: 'skills', label: 'Skills' },
    { id: 'tier', label: 'Tier' },
];

const STAT_KEYS = ['hp', 'atk', 'def'];

const RANK_LABELS = {
    1: 'SS',
    2: 'S',
    3: 'A+',
    4: 'A',
    5: 'A-',
    6: 'B+',
    7: 'B',
    8: 'B-',
    9: 'C+',
    10: 'C',
    11: 'C-',
    12: 'D',
    13: 'F',
};

function unitThumb(unit) {
    return unit.image?.thumbsuper || unit.image?.thumbawk || '';
}

function statTotal(unit, key) {
    const base = Number(unit?.stats?.[key] || 0);
    const plus = Number(unit?.stats?.[`${key}plus`] || 0);
    return base + plus;
}

function buildStatRankings(units) {
    const rankings = {};

    STAT_KEYS.forEach((key) => {
        const totals = units
            .filter((unit) => unit?.name && unit.name !== '???')
            .map((unit) => statTotal(unit, key))
            .filter((value) => Number.isFinite(value) && value > 0);

        const uniqueSorted = [...new Set(totals)].sort((a, b) => b - a);
        rankings[key] = new Map(uniqueSorted.map((value, index) => [value, index + 1]));
    });

    return rankings;
}

function getTrueWeaponNames(unit) {
    const tw = unit?.trueweapon;
    if (!tw) return [];
    if (tw.name) return [tw.name];

    return Object.keys(tw)
        .filter((key) => /^true\d+$/.test(key))
        .map((key) => tw[key]?.name)
        .filter(Boolean);
}

function ComparePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const leftName = searchParams.get('a') || '';
    const rightName = searchParams.get('b') || '';
    const statRankings = useMemo(() => buildStatRankings(getUnits()), []);

    const left = leftName ? getUnitByName(leftName) : null;
    const right = rightName ? getUnitByName(rightName) : null;

    const [section, setSection] = useState('overview');

    const setSlot = (slot, name) => {
        const next = {
            type: 'units',
            a: slot === 'a' ? name : leftName,
            b: slot === 'b' ? name : rightName,
        };
        if (!next.a) delete next.a;
        if (!next.b) delete next.b;
        setSearchParams(next);
    };

    const clearSlot = (slot) => setSlot(slot, '');

    return (
        <Container className="gs-page">
            <div className="gs-page-gutter">
                <BackToList to="/units" label="Back to Units" />

                <header className="gs-page-intro">
                    <h1>Compare Units</h1>
                    <p>Pick two units to compare side by side.</p>
                </header>

                <div className="gs-compare-pickers">
                    <ComparePicker
                        label="Left"
                        selectedName={leftName}
                        onSelect={(name) => setSlot('a', name)}
                        onClear={() => clearSlot('a')}
                        excludeName={rightName}
                    />
                    <div className="gs-compare-vs" aria-hidden="true">vs</div>
                    <ComparePicker
                        label="Right"
                        selectedName={rightName}
                        onSelect={(name) => setSlot('b', name)}
                        onClear={() => clearSlot('b')}
                        excludeName={leftName}
                    />
                </div>

                {(left || right) && (
                    <>
                        <nav className="gs-compare-tabs" aria-label="Compare sections">
                            {SECTIONS.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`gs-compare-tab${section === item.id ? ' is-active' : ''}`}
                                    onClick={() => setSection(item.id)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </nav>

                        {section === 'tier' && left && right && hasTierScores(left) && hasTierScores(right) && (
                            <TierRadarChart left={left} right={right} />
                        )}

                        <div className="gs-compare-columns">
                            <CompareColumn entity={left} otherEntity={right} section={section} emptyLabel="Select a unit" statRankings={statRankings} />
                            <CompareColumn entity={right} otherEntity={left} section={section} emptyLabel="Select a unit" statRankings={statRankings} />
                        </div>
                    </>
                )}
            </div>
        </Container>
    );
}

function ComparePicker({ label, selectedName, onSelect, onClear, excludeName }) {
    const [query, setQuery] = useState('');
    const searchRef = useDesktopAutofocus();

    const options = useMemo(() => {
        const list = getUnits();
        const q = query.trim().toLowerCase();
        return list
            .filter((item) => item.name && item.name !== '???')
            .filter((item) => item.name !== excludeName)
            .filter((item) => {
                if (!q) return true;
                if (item.name.toLowerCase().includes(q)) return true;
                if (item.translate?.toLowerCase().includes(q)) return true;
                return false;
            })
            .slice(0, 12);
    }, [query, excludeName]);

    return (
        <div className="gs-compare-picker">
            <div className="gs-compare-picker__head">
                <span className="gs-field-label">{label}</span>
                {selectedName ? (
                    <button type="button" className="gs-btn gs-btn--ghost" onClick={onClear}>
                        Clear
                    </button>
                ) : null}
            </div>
            {selectedName ? (
                <p className="gs-compare-picker__selected">{selectedName}</p>
            ) : (
                <>
                    <SearchInput
                        inputRef={searchRef}
                        placeholder="Search unit name"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onClear={() => setQuery('')}
                    />
                    {query.trim() && (
                        <ul className="gs-compare-picker__results">
                            {options.map((item) => (
                                <li key={item.id || item.name}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onSelect(item.name);
                                            setQuery('');
                                        }}
                                    >
                                        {item.name}
                                    </button>
                                </li>
                            ))}
                            {options.length === 0 && (
                                <li className="gs-compare-picker__empty">No matches</li>
                            )}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
}

function CompareColumn({ entity, otherEntity, section, emptyLabel, statRankings }) {
    if (!entity) {
        return (
            <div className="gs-compare-card gs-compare-card--empty">
                <p>{emptyLabel}</p>
            </div>
        );
    }
    return <UnitCompareCard unit={entity} otherUnit={otherEntity} section={section} statRankings={statRankings} />;
}

function UnitCompareCard({ unit, otherUnit, section, statRankings }) {
    const thumb = unitThumb(unit);
    const detailPath = `/units/${unit.name}`;
    const trueWeaponNames = getTrueWeaponNames(unit);

    return (
        <div className="gs-compare-card">
            <div className="gs-compare-card__header">
                {thumb ? <Media src={thumb} alt="" className="gs-compare-card__thumb" /> : null}
                <div className="gs-compare-card__info">
                    <h2>{unit.name}</h2>
                    <p className="gs-compare-card__sub">{unit.attribute} · {unit.type}</p>
                    <Link to={detailPath} className="gs-btn gs-compare-card__details">Full Details</Link>
                </div>
            </div>

            {section === 'overview' && (
                <div className="gs-compare-section">
                    <CompareRow label="Slots"><SlotRow slots={unit.slots} /></CompareRow>
                    <CompareRow label={trueWeaponNames.length > 1 ? 'True Weapons' : 'True Weapon'}>
                        {trueWeaponNames.length > 0 ? (
                            <span className="gs-compare-trueweapons">
                                {trueWeaponNames.map((name) => (
                                    <span key={name} className="gs-compare-trueweapons__item">{name}</span>
                                ))}
                            </span>
                        ) : (
                            <span className="gs-compare-empty">-</span>
                        )}
                    </CompareRow>
                    <CompareRow label="Dream Awakening">
                        {unit.dream ? 'Yes' : <span className="gs-compare-empty">No</span>}
                    </CompareRow>
                    <CompareRow label="Super Awakening">
                        {unit.image?.detailsuper ? 'Yes' : <span className="gs-compare-empty">No</span>}
                    </CompareRow>
                </div>
            )}

            {section === 'stats' && (
                <div className="gs-compare-section">
                    <StatRow label="HP" statKey="hp" unit={unit} otherUnit={otherUnit} statRankings={statRankings} />
                    <StatRow label="ATK" statKey="atk" unit={unit} otherUnit={otherUnit} statRankings={statRankings} />
                    <StatRow label="DEF" statKey="def" unit={unit} otherUnit={otherUnit} statRankings={statRankings} />
                    <CompareRow label="Slots"><SlotRow slots={unit.slots} /></CompareRow>
                </div>
            )}

            {section === 'skills' && (
                <div className="gs-compare-section gs-compare-section--skills">
                    <SkillBlock label="Skill" text={unit.skillset?.skill} breakValue={unit.skillset?.skillbreak ?? unit.skillset?.skillbreak1} />
                    {unit.skillset?.magiaskill && (
                        <SkillBlock label="Skill 2" text={unit.skillset.magiaskill} breakValue={unit.skillset?.skillbreak2} />
                    )}
                    <SkillBlock label="Arts" text={unit.skillset?.arts} breakValue={unit.skillset?.artsbreak} />
                    <SkillBlock label="Protagonize" text={unit.skillset?.protagonize} breakValue={unit.skillset?.protagonizebreak} />
                    <SkillBlock
                        label="True Arts"
                        text={unit.skillset?.truearts || unit.skillset?.truearts1}
                        breakValue={unit.skillset?.trueartsbreak ?? unit.skillset?.trueartsbreak1}
                    />
                    {unit.skillset?.truearts2 && (
                        <SkillBlock label="True Arts 2" text={unit.skillset.truearts2} breakValue={unit.skillset?.trueartsbreak2} />
                    )}
                    <SkillBlock label="Cross Arts" text={unit.skillset?.crossarts} breakValue={unit.skillset?.crossartsbreak} />
                    <SkillBlock
                        label={unit.skillset?.superequipname ? `Super Equip "${unit.skillset.superequipname}"` : 'Super Equip'}
                        text={unit.skillset?.superequip}
                        breakValue={unit.skillset?.superequipbreak}
                    />
                    <SkillBlock label="Super Arts" text={unit.skillset?.superarts} breakValue={unit.skillset?.superartsbreak} />
                    <SkillBlock label="Phantom Bullet" text={unit.skillset?.phantombullet} breakValue={unit.skillset?.phantombulletbreak} />
                    <SkillBlock label="EX Arts" text={unit.skillset?.exarts} breakValue={unit.skillset?.exartsbreak} />
                    <SkillBlock label="Origin Bullet" text={unit.skillset?.originbullet} breakValue={unit.skillset?.originbulletbreak} />
                    <PassiveList passives={unit.passive} />
                    <DreamPassiveList dream={unit.dream} />
                </div>
            )}

            {section === 'tier' && (
                hasTierScores(unit) ? (
                    <div className="gs-compare-section">
                        <CompareRow label="Rank">
                            <span className={`gs-tier-table__rank gs-tier-table__rank--${unit.tier.rank}`}>
                                {RANK_LABELS[unit.tier.rank] ?? '?'}
                            </span>
                        </CompareRow>
                        <ValueRow label="Defense" value={unit.tier?.defense} />
                        <ValueRow label="ArtsGen" value={unit.tier?.artgen} />
                        <ValueRow label="Damage" value={unit.tier?.damage} />
                        <ValueRow label="Buffs" value={unit.tier?.buffs} />
                        <ValueRow label="Heal" value={unit.tier?.heal} />
                        <ValueRow label="Break" value={unit.tier?.break} />
                    </div>
                ) : (
                    <div className="gs-compare-tier-empty">
                        <p>No tier scores available for this unit.</p>
                        <p>Only Global units have been scored and tiered.</p>
                    </div>
                )
            )}
        </div>
    );
}

function ValueRow({ label, value }) {
    if (value === undefined || value === null || value === '') return null;
    return (
        <CompareRow label={label}>
            {value}
        </CompareRow>
    );
}

function CompareRow({ label, children, rowClassName = '', valueClassName = '' }) {
    return (
        <div className={`gs-compare-row${rowClassName ? ` ${rowClassName}` : ''}`}>
            <span className="gs-compare-row__label">{label}</span>
            <span className={`gs-compare-row__value${valueClassName ? ` ${valueClassName}` : ''}`}>{children}</span>
        </div>
    );
}

function StatRow({ label, statKey, unit, otherUnit, statRankings }) {
    const value = unit?.stats?.[statKey];
    const plus = unit?.stats?.[`${statKey}plus`];
    if (value === undefined || value === null || value === '') return null;

    const ownTotal = statTotal(unit, statKey);
    const otherTotal = otherUnit ? statTotal(otherUnit, statKey) : null;
    const rank = statRankings?.[statKey]?.get(ownTotal);
    const isBetter = otherUnit && ownTotal > otherTotal;
    const isWorse = otherUnit && ownTotal < otherTotal;

    return (
        <CompareRow
            label={label}
            rowClassName={isBetter ? 'is-better' : isWorse ? 'is-worse' : ''}
            valueClassName="gs-compare-stat"
        >
            <span className="gs-compare-stat__main">
                <span className="gs-compare-stat__value">
                    {value}{plus ? <span className="gs-compare-row__plus"> (+{plus})</span> : ''}
                </span>
                {rank ? (
                    <span className={`gs-compare-stat__rank gs-compare-stat__rank--${Math.min(rank, 4)}`}>
                        {rank === 1 ? <span className="gs-compare-stat__crown" aria-hidden="true">♛</span> : null}
                        {rank === 2 ? <span className="gs-compare-stat__medal" aria-hidden="true">🥈</span> : null}
                        {rank === 3 ? <span className="gs-compare-stat__medal" aria-hidden="true">🥉</span> : null}
                        #{rank}
                    </span>
                ) : null}
            </span>
        </CompareRow>
    );
}

function SlotRow({ slots }) {
    if (!slots) return null;
    const hasAltSlot3 = Boolean(slots.slot31type && slots.slot32type);

    const mainSlots = [
        { star: slots.slot1, type: slots.slot1type },
        { star: slots.slot2, type: slots.slot2type },
        hasAltSlot3
            ? null
            : { star: slots.slot3, type: slots.slot3type },
    ].filter((s) => s && s.type);

    const altSlots = hasAltSlot3
        ? [
            { star: slots.slot3, type: slots.slot31type },
            { star: slots.slot3, type: slots.slot32type },
        ]
        : [];

    const Pill = ({ slot }) => (
        <span className="gs-slot-pill">
            <span className="gs-slot-pill__star">{slot.star}★</span>
            <img src={slot.type} alt="" className="gs-slot-pill__icon" />
        </span>
    );

    return (
        <span className="gs-compare-slots">
            {mainSlots.map((slot, i) => (
                <Pill key={`s-${i}`} slot={slot} />
            ))}
            {altSlots.length > 0 && (
                <span className="gs-slot-pair">
                    <Pill slot={altSlots[0]} />
                    <span className="gs-slot-pair__div">/</span>
                    <Pill slot={altSlots[1]} />
                </span>
            )}
        </span>
    );
}

function SkillBlock({ label, text, breakValue }) {
    if (!text) return null;
    const hasBreak = breakValue !== undefined && breakValue !== null && breakValue !== '';
    return (
        <div className="gs-compare-skill">
            <div className="gs-compare-skill__head">
                <span className="gs-compare-skill__label">{label}</span>
                {hasBreak && <span className="gs-badge gs-badge--accent gs-compare-skill__break">{breakValue} BREAK</span>}
            </div>
            <p className="gs-compare-skill__text">{text}</p>
        </div>
    );
}

function PassiveList({ passives }) {
    const items = Object.values(passives || {}).filter(Boolean);
    if (items.length === 0) return null;

    return (
        <div className="gs-compare-skill">
            <span className="gs-compare-skill__label">Unit Passives</span>
            <ul className="gs-compare-passive-list">
                {items.map((text, index) => (
                    <li key={`passive-${index}`}>{text}</li>
                ))}
            </ul>
        </div>
    );
}

const DREAM_PATHWAYS = [
    { key: 'fire', label: 'Crimson' },
    { key: 'water', label: 'Aqueous' },
    { key: 'earth', label: 'Verdant' },
    { key: 'light', label: 'Luminous' },
    { key: 'dark', label: 'Spectral' },
];

function DreamPassiveList({ dream }) {
    if (!dream || typeof dream !== 'object') return null;

    const pathways = DREAM_PATHWAYS
        .map(({ key, label }) => {
            const abilities = Object.values(dream[key]?.passive || {}).filter(Boolean);
            if (abilities.length === 0) return null;
            return { key, label, abilities };
        })
        .filter(Boolean);

    if (pathways.length === 0) return null;

    return (
        <div className="gs-compare-skill">
            <span className="gs-compare-skill__label">Dream Passives</span>
            <div className="gs-compare-dream-paths">
                {pathways.map(({ key, label, abilities }) => (
                    <div
                        key={key}
                        className={`gs-compare-dream-path gs-compare-dream-path--${key}`}
                    >
                        <div className="gs-compare-dream-path__head">
                            <span className="gs-compare-dream-path__radio" aria-hidden="true" />
                            <span className="gs-compare-dream-path__name">{label}</span>
                        </div>
                        <ul className="gs-compare-passive-list">
                            {abilities.map((text, index) => (
                                <li key={`${key}-ability-${index}`}>{text}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
}

const RADAR_AXES = [
    { key: 'defense', label: 'Defense', get: (unit) => Number(unit?.tier?.defense) || 0 },
    { key: 'artgen', label: 'ArtsGen', get: (unit) => Number(unit?.tier?.artgen) || 0 },
    { key: 'damage', label: 'Damage', get: (unit) => Number(unit?.tier?.damage) || 0 },
    { key: 'buffs', label: 'Buffs', get: (unit) => Number(unit?.tier?.buffs) || 0 },
    { key: 'heal', label: 'Heal', get: (unit) => Number(unit?.tier?.heal) || 0 },
    { key: 'break', label: 'Break', get: (unit) => Number(unit?.tier?.break) || 0 },
];

function hasTierScores(unit) {
    const tier = unit?.tier;
    if (!tier || typeof tier !== 'object') return false;
    return RADAR_AXES.some((axis) => Number.isFinite(Number(tier[axis.key])));
}

function radarPoint(index, count, radius, cx, cy) {
    const angle = (-Math.PI / 2) + (index * 2 * Math.PI) / count;
    return {
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle),
    };
}

function radarWedgePath(index, count, radius, cx, cy) {
    const start = (-Math.PI / 2) + ((index - 0.5) * 2 * Math.PI) / count;
    const end = (-Math.PI / 2) + ((index + 0.5) * 2 * Math.PI) / count;
    const startPoint = {
        x: cx + radius * Math.cos(start),
        y: cy + radius * Math.sin(start),
    };
    const endPoint = {
        x: cx + radius * Math.cos(end),
        y: cy + radius * Math.sin(end),
    };
    return [
        `M ${cx} ${cy}`,
        `L ${startPoint.x} ${startPoint.y}`,
        `A ${radius} ${radius} 0 0 1 ${endPoint.x} ${endPoint.y}`,
        'Z',
    ].join(' ');
}

function formatRadarScore(value) {
    if (!Number.isFinite(value)) return '—';
    return Number(value.toFixed(2)).toString();
}

function buildRadarPoints(values, radius, cx, cy, maxScale = 5) {
    return values.map((value, index) => {
        const ratio = Math.max(0, Math.min(1, value / maxScale));
        const scaled = value > 0 ? Math.max(0.06, ratio) : 0;
        return radarPoint(index, values.length, radius * scaled, cx, cy);
    });
}

function TierRadarChart({ left, right }) {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const size = 320;
    const cx = size / 2;
    const cy = size / 2;
    const radius = size * 0.34;
    const hitRadius = radius * 1.28;
    const rings = [0.25, 0.5, 0.75, 1];
    const leftValues = RADAR_AXES.map((axis) => axis.get(left));
    const rightValues = RADAR_AXES.map((axis) => axis.get(right));
    const leftPoints = buildRadarPoints(leftValues, radius, cx, cy);
    const rightPoints = buildRadarPoints(rightValues, radius, cx, cy);
    const toPointsAttr = (points) => points.map((point) => `${point.x},${point.y}`).join(' ');
    const hoveredAxis = hoveredIndex !== null ? RADAR_AXES[hoveredIndex] : null;
    const tooltipPoint = hoveredIndex !== null
        ? radarPoint(hoveredIndex, RADAR_AXES.length, radius * 1.08, cx, cy)
        : null;

    return (
        <section className="gs-compare-radar" aria-label="Tier radar comparison">
            <div className="gs-compare-radar__legend">
                <span className="gs-compare-radar__legend-item">
                    <span className="gs-compare-radar__swatch gs-compare-radar__swatch--left" />
                    <span className="gs-compare-radar__name">{left.name}</span>
                </span>
                <span className="gs-compare-radar__vs">vs</span>
                <span className="gs-compare-radar__legend-item">
                    <span className="gs-compare-radar__swatch gs-compare-radar__swatch--right" />
                    <span className="gs-compare-radar__name">{right.name}</span>
                </span>
            </div>

            <div
                className="gs-compare-radar__canvas"
                onMouseLeave={() => setHoveredIndex(null)}
            >
                <svg
                    className="gs-compare-radar__svg"
                    viewBox={`0 0 ${size} ${size}`}
                    role="group"
                    aria-label={`Radar chart comparing ${left.name} and ${right.name} tier scores`}
                >
                    <defs>
                        <radialGradient id="gsRadarGlow" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="rgba(34, 187, 255, 0.16)" />
                            <stop offset="70%" stopColor="rgba(34, 187, 255, 0.04)" />
                            <stop offset="100%" stopColor="rgba(34, 187, 255, 0)" />
                        </radialGradient>
                    </defs>

                    <circle className="gs-compare-radar__glow" cx={cx} cy={cy} r={radius * 1.05} fill="url(#gsRadarGlow)" />

                    {rings.map((scale) => (
                        <circle
                            key={scale}
                            className="gs-compare-radar__ring"
                            cx={cx}
                            cy={cy}
                            r={radius * scale}
                        />
                    ))}

                    {RADAR_AXES.map((axis, index) => {
                        const tip = radarPoint(index, RADAR_AXES.length, radius, cx, cy);
                        const label = radarPoint(index, RADAR_AXES.length, radius * 1.22, cx, cy);
                        const isActive = hoveredIndex === index;
                        return (
                            <g key={axis.key} className={isActive ? 'is-active' : hoveredIndex !== null ? 'is-dimmed' : ''}>
                                <line
                                    className={`gs-compare-radar__axis${isActive ? ' is-active' : ''}`}
                                    x1={cx}
                                    y1={cy}
                                    x2={tip.x}
                                    y2={tip.y}
                                />
                                <text
                                    className={`gs-compare-radar__label${isActive ? ' is-active' : ''}`}
                                    x={label.x}
                                    y={label.y}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                >
                                    {axis.label}
                                </text>
                            </g>
                        );
                    })}

                    <polygon
                        className="gs-compare-radar__shape gs-compare-radar__shape--right"
                        points={toPointsAttr(rightPoints)}
                    />
                    <polygon
                        className="gs-compare-radar__shape gs-compare-radar__shape--left"
                        points={toPointsAttr(leftPoints)}
                    />

                    {rightPoints.map((point, index) => (
                        <circle
                            key={`right-dot-${index}`}
                            className={`gs-compare-radar__dot gs-compare-radar__dot--right${hoveredIndex === index ? ' is-active' : ''}`}
                            cx={point.x}
                            cy={point.y}
                            r={hoveredIndex === index ? 5.5 : 3.5}
                        />
                    ))}
                    {leftPoints.map((point, index) => (
                        <circle
                            key={`left-dot-${index}`}
                            className={`gs-compare-radar__dot gs-compare-radar__dot--left${hoveredIndex === index ? ' is-active' : ''}`}
                            cx={point.x}
                            cy={point.y}
                            r={hoveredIndex === index ? 5.5 : 3.5}
                        />
                    ))}

                    {RADAR_AXES.map((axis, index) => (
                        <path
                            key={`hit-${axis.key}`}
                            className="gs-compare-radar__hit"
                            d={radarWedgePath(index, RADAR_AXES.length, hitRadius, cx, cy)}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onFocus={() => setHoveredIndex(index)}
                            onBlur={() => setHoveredIndex(null)}
                            tabIndex={0}
                            role="button"
                            aria-label={`${axis.label}: ${left.name} ${formatRadarScore(leftValues[index])}, ${right.name} ${formatRadarScore(rightValues[index])}`}
                        />
                    ))}
                </svg>

                {hoveredAxis && tooltipPoint && (
                    <div
                        className="gs-compare-radar__tooltip"
                        style={{
                            left: `${(tooltipPoint.x / size) * 100}%`,
                            top: `${(tooltipPoint.y / size) * 100}%`,
                        }}
                        role="tooltip"
                    >
                        <div className="gs-compare-radar__tooltip-title">{hoveredAxis.label}</div>
                        <div className="gs-compare-radar__tooltip-row">
                            <span className="gs-compare-radar__tooltip-swatch gs-compare-radar__swatch--left" />
                            <span className="gs-compare-radar__tooltip-name">{left.name}</span>
                            <span className="gs-compare-radar__tooltip-value">{formatRadarScore(leftValues[hoveredIndex])}</span>
                        </div>
                        <div className="gs-compare-radar__tooltip-row">
                            <span className="gs-compare-radar__tooltip-swatch gs-compare-radar__swatch--right" />
                            <span className="gs-compare-radar__tooltip-name">{right.name}</span>
                            <span className="gs-compare-radar__tooltip-value">{formatRadarScore(rightValues[hoveredIndex])}</span>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

export default ComparePage;
